import type { ExteriorPalette, FullExteriorPalette } from "building-types/materials/exteriorPaletteTypes";
import { PaletteType } from "building-types/materials/paletteType";
import type { ExteriorMaterialSettings } from "building-types/settings/exteriorMaterialSettings";
import { SettingsType } from "building-types/settings/settingsType";
import {
  MS_Color__C,
  MS_Floor_Concrete__Default,
  MS_Glass_Base__Default,
  MS_Grass_Lawn_01__Default,
  MS_Gravel__Default,
  MS_Metal_Screen__Default,
} from "common/materials/library/designMaterialSrcDefaults";
import { DMC } from "common/materials/library/DMC";
import { MasonryIdByName } from "common/materials/library/masonryIdByName";
import uid from "common/utils/uid";
import { BuiltinHomeSettingsId } from "./builtinHomeSettingsId";

export const defaultExteriorMaterials: FullExteriorPalette = {
  wallExterior0: { ...MS_Color__C, colorId: DMC.SystemColors.Light_Brown },
  wallExterior1: { ...MS_Color__C, colorId: DMC.SystemColors.Brown },
  wallExterior2: { ...MS_Color__C, colorId: DMC.SystemColors.Dark_Brown },
  wallExterior3: { ...MS_Color__C, colorId: DMC.SystemColors.Darkest_Brown },
  // Intentionally not defining wallExterior4-9, there's questionable value for defining colors for each.

  trimExterior0: { ...MS_Color__C, colorId: DMC.SystemColors.Light_Purple },
  trimExterior1: { ...MS_Color__C, colorId: DMC.SystemColors.Purple },
  trimExterior2: { ...MS_Color__C, colorId: DMC.SystemColors.Dark_Purple },
  trimExterior3: { ...MS_Color__C, colorId: DMC.SystemColors.Darkest_Purple },
  // Intentionally not defining trimExterior4-9, there's questionable value for defining colors for each.

  roof0: { ...MS_Color__C, colorId: DMC.SystemColors.Light_Blue },
  roof1: { ...MS_Color__C, colorId: DMC.SystemColors.Blue },
  roof2: { ...MS_Color__C, colorId: DMC.SystemColors.Dark_Blue },
  roof3: { ...MS_Color__C, colorId: DMC.SystemColors.Darkest_Blue },

  roofTrim0: { ...MS_Color__C, colorId: DMC.SystemColors.Light_Yellow },
  roofTrim1: { ...MS_Color__C, colorId: DMC.SystemColors.Yellow },
  roofTrim2: { ...MS_Color__C, colorId: DMC.SystemColors.Dark_Yellow },
  roofTrim3: { ...MS_Color__C, colorId: DMC.SystemColors.Darkest_Yellow },

  gutter0: { ...MS_Color__C, colorId: DMC.SystemColors.Light_Cream },
  gutter1: { ...MS_Color__C, colorId: DMC.SystemColors.Cream },
  gutter2: { ...MS_Color__C, colorId: DMC.SystemColors.Dark_Cream },

  foundation0: { ...MS_Color__C, colorId: DMC.SystemColors.Light_Orange },
  foundation1: { ...MS_Color__C, colorId: DMC.SystemColors.Orange },
  foundation2: { ...MS_Color__C, colorId: DMC.SystemColors.Dark_Orange },

  driveway0: { ...MS_Color__C, colorId: DMC.SystemColors.Light_Purple },
  driveway1: { ...MS_Color__C, colorId: DMC.SystemColors.Purple },
  driveway2: { ...MS_Color__C, colorId: DMC.SystemColors.Dark_Purple },

  walks0: { ...MS_Color__C, colorId: DMC.SystemColors.Light_Yellow },
  walks1: { ...MS_Color__C, colorId: DMC.SystemColors.Yellow },
  walks2: { ...MS_Color__C, colorId: DMC.SystemColors.Dark_Yellow },

  porchCeiling0: { ...MS_Color__C, colorId: DMC.SystemColors.Light_Red },
  porchCeiling1: { ...MS_Color__C, colorId: DMC.SystemColors.Red },
  porchCeiling2: { ...MS_Color__C, colorId: DMC.SystemColors.Dark_Red },

  deck0: { ...MS_Color__C, colorId: DMC.SystemColors.Light_Red },
  deck1: { ...MS_Color__C, colorId: DMC.SystemColors.Red },
  deck2: { ...MS_Color__C, colorId: DMC.SystemColors.Dark_Red },
  deck3: { ...MS_Color__C, colorId: DMC.SystemColors.Darkest_Red },

  glass0: MS_Glass_Base__Default,
  glass1: { ...MS_Color__C, colorId: DMC.SystemColors.Turquoise },
  glass2: MS_Metal_Screen__Default,

  glassTrim0: { ...MS_Color__C, colorId: DMC.SystemColors.Light_Green },
  glassTrim1: { ...MS_Color__C, colorId: DMC.SystemColors.Green },
  glassTrim2: { ...MS_Color__C, colorId: DMC.SystemColors.Dark_Green },

  doorExterior0: { ...MS_Color__C, colorId: DMC.SystemColors.Light_Red },
  doorExterior1: { ...MS_Color__C, colorId: DMC.SystemColors.Red },
  doorExterior2: { ...MS_Color__C, colorId: DMC.SystemColors.Dark_Red },
  doorExterior3: { ...MS_Color__C, colorId: DMC.SystemColors.Darkest_Red },

  garageExterior0: { ...MS_Color__C, colorId: DMC.SystemColors.Light_Red },
  garageExterior1: { ...MS_Color__C, colorId: DMC.SystemColors.Red },
  garageExterior2: { ...MS_Color__C, colorId: DMC.SystemColors.Dark_Red },
  garageExterior3: { ...MS_Color__C, colorId: DMC.SystemColors.Darkest_Red },

  hardwareExterior0: { ...MS_Color__C, colorId: DMC.SystemColors.Light_Turquoise },
  hardwareExterior1: { ...MS_Color__C, colorId: DMC.SystemColors.Turquoise },
  hardwareExterior2: { ...MS_Color__C, colorId: DMC.SystemColors.Dark_Turquoise },

  propDefault0: { ...MS_Color__C, colorId: DMC.SystemColors.White },
  propDefault1: { ...MS_Color__C, colorId: DMC.SystemColors.Gray80 },
  propDefault2: { ...MS_Color__C, colorId: DMC.SystemColors.Gray55 },

  chimney0: { ...MS_Color__C, colorId: DMC.SystemColors.Light_Red },
  chimney1: { ...MS_Color__C, colorId: DMC.SystemColors.Red },
  chimney2: { ...MS_Color__C, colorId: DMC.SystemColors.Dark_Red },

  masonry0: { ...MS_Color__C, colorId: DMC.SystemColors.Light_Turquoise },
  masonry1: { ...MS_Color__C, colorId: DMC.SystemColors.Turquoise },
  masonry2: { ...MS_Color__C, colorId: DMC.SystemColors.Dark_Turquoise },
  masonry3: { ...MS_Color__C, colorId: DMC.SystemColors.Very_Dark_Turquoise },

  masonryTrim0: MasonryIdByName.TrimColorCodes,
  masonryTrim1: MasonryIdByName.TrimColorCodes,
  masonryTrim2: MasonryIdByName.TrimColorCodes,
  masonryTrim3: MasonryIdByName.TrimColorCodes,

  shutter0: { ...MS_Color__C, colorId: DMC.SystemColors.Light_Red },
  shutter1: { ...MS_Color__C, colorId: DMC.SystemColors.Red },
  shutter2: { ...MS_Color__C, colorId: DMC.SystemColors.Dark_Red },

  headCasing0: { ...MS_Color__C, colorId: DMC.SystemColors.Light_Turquoise },
  headCasing1: { ...MS_Color__C, colorId: DMC.SystemColors.Turquoise },
  headCasing2: { ...MS_Color__C, colorId: DMC.SystemColors.Dark_Turquoise },
  headCasing3: { ...MS_Color__C, colorId: DMC.SystemColors.Darkest_Turquoise },

  apronCasing0: { ...MS_Color__C, colorId: DMC.SystemColors.Light_Blue },
  apronCasing1: { ...MS_Color__C, colorId: DMC.SystemColors.Blue },
  apronCasing2: { ...MS_Color__C, colorId: DMC.SystemColors.Dark_Blue },
  apronCasing3: { ...MS_Color__C, colorId: DMC.SystemColors.Darkest_Blue },

  sideCasing0: { ...MS_Color__C, colorId: DMC.SystemColors.Light_Purple },
  sideCasing1: { ...MS_Color__C, colorId: DMC.SystemColors.Purple },
  sideCasing2: { ...MS_Color__C, colorId: DMC.SystemColors.Dark_Purple },
  sideCasing3: { ...MS_Color__C, colorId: DMC.SystemColors.Darkest_Purple },

  earth0: { ...MS_Color__C, colorId: DMC.SystemColors.Dark_Brown },
  road0: { ...MS_Gravel__Default },
  grass0: { ...MS_Grass_Lawn_01__Default },
  sidewalk0: { ...MS_Floor_Concrete__Default },
  curb0: { ...MS_Floor_Concrete__Default },
  fence0: { ...MS_Color__C, colorId: DMC.SystemColors.Brown },
  retainingWall0: { ...MS_Floor_Concrete__Default },
  water0: { ...MS_Color__C, colorId: DMC.SystemColors.Light_Blue },
};

export const defaultExteriorPalette = (params: Partial<ExteriorPalette> = {}): ExteriorPalette => ({
  id: uid(),
  entityType: "PALETTE",
  sortIndex: 0,
  type: PaletteType.EXTERIOR_PALETTE,
  name: BuiltinHomeSettingsId.Default,
  ...defaultExteriorMaterials,
  ...params,
});

export const defaultExteriorMaterialSettings: ExteriorMaterialSettings = {
  type: SettingsType.EXTERIOR_MATERIAL_SETTINGS,
  name: BuiltinHomeSettingsId.Default_01,
  id: BuiltinHomeSettingsId.Default_01 + "_exterior",
  palettes: [BuiltinHomeSettingsId.Default_01],
  paletteSlotsUserDefinedNames: {
    [BuiltinHomeSettingsId.Default_01]: {},
  },
};
